import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Sparkles, Plus, Trash2, Pencil, Loader2, Save, CheckCircle2, XCircle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api, { pdvApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import HaccpConsultantHeader from '../components/HaccpConsultantHeader';

const AREE_DEFAULT = ['Cucina', 'Bagno', 'Spogliatoio', 'Cella frigorifera', 'Banco vendita', 'Magazzino', 'Reparto carni', 'Reparto gastronomia'];

const emptyEntry = () => ({
  data: new Date().toISOString().slice(0, 10),
  ora: '',
  area: '',
  attivita: 'Pulizia ordinaria',
  prodotto: '',
  diluizione: '',
  operatore: '',
  controllo_visivo: true,
  note: '',
});

const emptyForm = () => ({
  pdv_id: '',
  titolo: '',
  periodo_dal: new Date().toISOString().slice(0, 10),
  periodo_al: new Date().toISOString().slice(0, 10),
  aree_csv: AREE_DEFAULT.join('; '),
  voci: [emptyEntry()],
  note: '',
});

export default function HaccpCleaningPage() {
  const { user } = useAuth();
  const { currentPdvId } = useCurrentPdv();
  const [pdvList, setPdvList] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([pdvApi.getAll(), api.get('/haccp-cleaning')]);
      setPdvList(p.data);
      setRecords(r.data);
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    const f = emptyForm();
    // Pre-seleziona la UL "corrente" (impostata cliccando una UL nella lista)
    if (currentPdvId && pdvList.some(p => p.id === currentPdvId)) {
      f.pdv_id = currentPdvId;
    } else if (pdvList.length === 1) {
      f.pdv_id = pdvList[0].id;
    }
    setForm(f);
    setDialog(true);
  };
  const openEdit = async (rec) => {
    try {
      const full = await api.get(`/haccp-cleaning/${rec.id}`);
      const d = full.data;
      setForm({
        pdv_id: d.pdv_id || '',
        titolo: d.titolo || '',
        periodo_dal: d.periodo_dal || '',
        periodo_al: d.periodo_al || '',
        aree_csv: (d.aree || AREE_DEFAULT).join('; '),
        voci: (d.voci || [emptyEntry()]).map(v => ({ ...emptyEntry(), ...v })),
        note: d.note || '',
      });
      setEditingId(rec.id);
      setDialog(true);
    } catch (e) { toast.error('Errore'); }
  };
  const handleSave = async () => {
    if (!form.pdv_id) { toast.error('Seleziona UL'); return; }
    setSaving(true);
    try {
      const aree = form.aree_csv.split(/[;,]+/).map(s => s.trim()).filter(Boolean);
      const payload = { ...form, aree, pdv_id: form.pdv_id };
      delete payload.aree_csv;
      if (editingId) await api.put(`/haccp-cleaning/${editingId}`, payload);
      else await api.post('/haccp-cleaning', payload);
      toast.success(editingId ? 'Aggiornato' : 'Creato');
      setDialog(false); load();
    } catch (e) { toast.error('Errore'); }
    finally { setSaving(false); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try { await api.delete(`/haccp-cleaning/${id}`); toast.success('Eliminato'); load(); }
    catch (e) { toast.error('Errore'); }
  };

  const addEntry = () => setForm(p => ({ ...p, voci: [...p.voci, emptyEntry()] }));
  const removeEntry = (i) => setForm(p => ({ ...p, voci: p.voci.filter((_, idx) => idx !== i) }));
  const setEntry = (i, patch) => setForm(p => { const n = [...p.voci]; n[i] = { ...n[i], ...patch }; return { ...p, voci: n }; });

  const pdvName = (id) => pdvList.find(p => p.id === id)?.name || '—';
  const aree = (form.aree_csv || '').split(/[;,]+/).map(s => s.trim()).filter(Boolean);
  const selectedPdv = pdvList.find(p => p.id === form.pdv_id);

  return (
    <div className="p-6 space-y-6" data-testid="haccp-cleaning-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-teal-500" />
            HACCP — Pulizia e Sanificazione
          </h1>
          <p className="text-gray-600 mt-1">Registra pulizie e sanificazioni con prodotto, area, operatore e controllo visivo.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-cleaning-btn"><Plus className="w-4 h-4 mr-1" /> Nuova Scheda</Button>
      </div>

      {loading ? <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        : records.length === 0 ? <Card><CardContent className="py-10 text-center text-gray-500">Nessuna scheda.</CardContent></Card>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {records.map((r) => (
              <Card key={r.id}>
                <CardHeader><CardTitle className="text-base">{r.titolo || `Scheda ${pdvName(r.pdv_id)}`}</CardTitle></CardHeader>
                <CardContent>
                  <Badge variant="outline" className="mb-2">{pdvName(r.pdv_id)}</Badge>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>📅 {r.periodo_dal} → {r.periodo_al}</div>
                    <div>🧴 {(r.voci || []).length} sanificazioni</div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="cleaning-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica Pulizia/Sanificazione' : 'Nuova Pulizia/Sanificazione'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <HaccpConsultantHeader pdv={selectedPdv} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label>Unità Locale *</Label>
                <Select value={form.pdv_id} onValueChange={(v) => setForm({ ...form, pdv_id: v })}>
                  <SelectTrigger data-testid="ul-select"><SelectValue placeholder="Seleziona UL" /></SelectTrigger>
                  <SelectContent>{pdvList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Titolo</Label><Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="es. Settimana 21" /></div>
              <div><Label>Periodo dal</Label><Input type="date" value={form.periodo_dal} onChange={(e) => setForm({ ...form, periodo_dal: e.target.value })} /></div>
              <div><Label>Periodo al</Label><Input type="date" value={form.periodo_al} onChange={(e) => setForm({ ...form, periodo_al: e.target.value })} /></div>
              <div className="lg:col-span-4"><Label>Aree (separate da ;)</Label><Input value={form.aree_csv} onChange={(e) => setForm({ ...form, aree_csv: e.target.value })} /></div>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between mb-2">
                <h3 className="font-semibold">Voci ({form.voci.length})</h3>
                <Button size="sm" variant="outline" onClick={addEntry}><Plus className="w-4 h-4 mr-1" /> Aggiungi</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className="bg-gray-100 text-left">
                    <th className="p-2 w-8">#</th><th className="p-2 min-w-[120px]">Data</th><th className="p-2 min-w-[90px]">Ora</th>
                    <th className="p-2 min-w-[140px]">Area</th><th className="p-2 min-w-[160px]">Attività</th>
                    <th className="p-2 min-w-[140px]">Prodotto</th><th className="p-2 min-w-[110px]">Diluizione</th>
                    <th className="p-2 min-w-[90px]">Op.</th><th className="p-2 min-w-[80px]">Controllo</th><th className="p-2 w-8"></th>
                  </tr></thead>
                  <tbody>
                    {form.voci.map((v, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1 text-gray-500">{i + 1}</td>
                        <td className="p-1"><Input type="date" value={v.data} onChange={(e) => setEntry(i, { data: e.target.value })} /></td>
                        <td className="p-1"><Input type="time" value={v.ora} onChange={(e) => setEntry(i, { ora: e.target.value })} /></td>
                        <td className="p-1">
                          <Select value={v.area || '__c__'} onValueChange={(x) => setEntry(i, { area: x === '__c__' ? '' : x })}>
                            <SelectTrigger><SelectValue placeholder="Area" /></SelectTrigger>
                            <SelectContent>{aree.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}<SelectItem value="__c__">— Altro —</SelectItem></SelectContent>
                          </Select>
                        </td>
                        <td className="p-1"><Input value={v.attivita} onChange={(e) => setEntry(i, { attivita: e.target.value })} /></td>
                        <td className="p-1"><Input value={v.prodotto} onChange={(e) => setEntry(i, { prodotto: e.target.value })} /></td>
                        <td className="p-1"><Input value={v.diluizione} onChange={(e) => setEntry(i, { diluizione: e.target.value })} placeholder="1%" /></td>
                        <td className="p-1"><Input value={v.operatore} onChange={(e) => setEntry(i, { operatore: e.target.value })} /></td>
                        <td className="p-1 text-center">
                          <button type="button" onClick={() => setEntry(i, { controllo_visivo: !v.controllo_visivo })}>
                            {v.controllo_visivo ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                          </button>
                        </td>
                        <td className="p-1"><Button size="sm" variant="ghost" onClick={() => removeEntry(i)} disabled={form.voci.length <= 1}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
